import { Injectable } from "@angular/core";
import {
  AuctionWithRelations,
  ExhibitionWithRelations,
  InventoryWithRelations,
  Item,
  ItemWithRelations,
  LocationWithRelations,
  PublicationWithRelations,
  SeriesWithRelations,
  TagGroupWithRelations,
} from "../api/models";
import {
  AdminControllerService,
  AdminInventoryControllerService,
  AdminItemsControllerService,
  InventoryAuctionsControllerService,
  InventoryControllerService,
  InventoryExhibitionsControllerService,
  InventoryPublicationsControllerService,
} from "../api/services";
import { BehaviorSubject, Observable, lastValueFrom } from "rxjs";
import { SortDirection } from "@angular/material/sort";
import { SettingsService } from "../shared/services/settings.service";

export interface InventoryState {
  mode?: string;
  tabIndex?: number;
  currentItemId?: string;
  itemsSort?: {
    active: string;
    direction: SortDirection;
  };
  managerSearchSettings?: ItemSearchSettings;
  gallerySearchSettings?: ItemSearchSettings;
}

export interface InventoryLoadingState {
  inventory: boolean;
  items: boolean;
  tags: boolean;
  locations: boolean;
  series: boolean;
}

export interface ItemSearchSettings {
  general: {
    caseSensitive: boolean;
    ignoreDiacritics: boolean;
    allowPartialMatch: boolean;
  };
  fields: {
    inventoryCode: boolean;
    originalTitle: boolean;
    authors: boolean;
    titleCS: boolean;
    titleEN: boolean;
    descriptionCS: boolean;
    descriptionEN: boolean;
    techniqueCS: boolean;
    techniqueEN: boolean;
    substrateCS: boolean;
    substrateEN: boolean;
    dating: boolean;
    datingTextCS: boolean;
    datingTextEN: boolean;
  };
  other: {
    titleOther: boolean;
    notesInternal: boolean;
  };
}

export const DEFAULT_SEARCH_SETTINGS: ItemSearchSettings = {
  general: {
    caseSensitive: false,
    ignoreDiacritics: true,
    allowPartialMatch: true,
  },
  fields: {
    inventoryCode: true,
    originalTitle: true,
    authors: true,
    titleCS: true,
    titleEN: false,
    descriptionCS: true,
    descriptionEN: false,
    techniqueCS: true,
    techniqueEN: false,
    substrateCS: true,
    substrateEN: false,
    dating: true,
    datingTextCS: true,
    datingTextEN: false,
  },
  other: {
    titleOther: true,
    notesInternal: true,
  },
};

@Injectable({
  providedIn: "root",
})
export class InventoryService {
  private loading: BehaviorSubject<InventoryLoadingState> =
    new BehaviorSubject<InventoryLoadingState>({
      inventory: false,
      items: false,
      tags: false,
      locations: false,
      series: false,
    });
  loading$: Observable<InventoryLoadingState> = this.loading.asObservable();

  private inventory: BehaviorSubject<InventoryWithRelations | undefined> =
    new BehaviorSubject<InventoryWithRelations | undefined>(undefined);
  inventory$: Observable<InventoryWithRelations | undefined> =
    this.inventory.asObservable();

  private items: BehaviorSubject<Item[] | undefined> = new BehaviorSubject<
    Item[] | undefined
  >(undefined);
  items$: Observable<Item[] | undefined> = this.items.asObservable();

  private includeRemoved = false;

  private tagGroups: BehaviorSubject<TagGroupWithRelations[] | undefined> =
    new BehaviorSubject<TagGroupWithRelations[] | undefined>(undefined);
  tagGroups$: Observable<TagGroupWithRelations[] | undefined> =
    this.tagGroups.asObservable();

  private locations: BehaviorSubject<LocationWithRelations[] | undefined> =
    new BehaviorSubject<LocationWithRelations[] | undefined>(undefined);
  locations$: Observable<LocationWithRelations[] | undefined> =
    this.locations.asObservable();

  private locationsFlat: BehaviorSubject<LocationWithRelations[] | undefined> =
    new BehaviorSubject<LocationWithRelations[] | undefined>(undefined);
  locationsFlat$: Observable<LocationWithRelations[] | undefined> =
    this.locationsFlat.asObservable();

  private series: BehaviorSubject<SeriesWithRelations[] | undefined> =
    new BehaviorSubject<SeriesWithRelations[] | undefined>(undefined);
  series$: Observable<SeriesWithRelations[] | undefined> =
    this.series.asObservable();

  private auctions: BehaviorSubject<AuctionWithRelations[] | undefined> =
    new BehaviorSubject<AuctionWithRelations[] | undefined>(undefined);
  auctions$: Observable<AuctionWithRelations[] | undefined> =
    this.auctions.asObservable();

  private exhibitions: BehaviorSubject<ExhibitionWithRelations[] | undefined> =
    new BehaviorSubject<ExhibitionWithRelations[] | undefined>(undefined);
  exhibitions$: Observable<ExhibitionWithRelations[] | undefined> =
    this.exhibitions.asObservable();

  private publications: BehaviorSubject<
    PublicationWithRelations[] | undefined
  > = new BehaviorSubject<PublicationWithRelations[] | undefined>(undefined);
  publications$: Observable<PublicationWithRelations[] | undefined> =
    this.publications.asObservable();

  state: InventoryState = {};

  constructor(
    private itemService: AdminItemsControllerService,
    private inventoryService: InventoryControllerService,
    private adminInventoryService: AdminInventoryControllerService,
    private auctionsService: InventoryAuctionsControllerService,
    private exhibitionsService: InventoryExhibitionsControllerService,
    private publicationsService: InventoryPublicationsControllerService,
    private settingsService: SettingsService,
  ) {}

  async loadInventory(
    identifiedBy: "id" | "code",
    identifier: string,
    forceReload = false,
  ): Promise<void> {
    if (!this.inventory.value || forceReload) {
      this.loading.next({
        ...this.loading.value,
        items: true,
        inventory: true,
      });
      const inventory = await lastValueFrom(
        this.inventoryService.getInventoryWithRelations({
          identifiedBy,
          identifier,
          includeRemoved: this.includeRemoved,
        }),
      );
      await this.settingsService.loadInventoryPreferences(inventory.id);
      this.items.next(inventory.items ? inventory.items : []);
      delete inventory.items;
      this.inventory.next(inventory);
      this.loading.next({
        ...this.loading.value,
        items: false,
        inventory: false,
      });
    }
  }

  /**
   * Clears the current inventory data and resets all related observables and state.
   *
   * This method performs the following actions:
   * 1. Clears the cached inventory, items, tag groups, locations, and series by setting them to undefined.
   * 2. Resets the loading state flags for inventory, items, tags, locations, and series to false.
   * 3. Resets the internal state object.
   */
  clearInventory(): void {
    // Reset the inventory data.
    this.inventory.next(undefined);
    // Reset the items list.
    this.items.next(undefined);
    this.includeRemoved = false;
    // Reset the tag groups.
    this.tagGroups.next(undefined);
    // Reset the locations data.
    this.locations.next(undefined);
    // Reset the series data.
    this.series.next(undefined);
    // Reset auctions, exhibitions, publications.
    this.auctions.next(undefined);
    this.exhibitions.next(undefined);
    this.publications.next(undefined);
    // Reset the loading state for all modules.
    this.loading.next({
      inventory: false,
      items: false,
      tags: false,
      locations: false,
      series: false,
    });
    // Clear the internal state object.
    this.state = {};
  }

  getInventory(): InventoryWithRelations {
    return this.inventory.value;
  }

  async loadItems(forceReload = false): Promise<void> {
    if ((!this.items.value || forceReload) && this.inventory.value) {
      this.items.next(undefined);
      this.loading.next({ ...this.loading.value, items: true });
      const items = await lastValueFrom(
        this.itemService.adminGetInventoryItems({
          inventoryId: this.inventory.value.id,
          includeRemoved: this.includeRemoved,
        }),
      );
      this.items.next(items);
      this.loading.next({ ...this.loading.value, items: false });
    }
  }

  async setIncludeRemoved(value: boolean): Promise<void> {
    this.includeRemoved = value;
    await this.loadItems(true);
  }

  getIncludeRemoved(): boolean {
    return this.includeRemoved;
  }

  getItems(): Item[] | undefined {
    return this.items.value;
  }

  /**
   * Updates the internal state with the given item ID as the current item.
   *
   * @param itemId - The ID of the item to set as current.
   */
  setCurrentItemId(itemId: string): void {
    // Set the current item ID in the state.
    this.state.currentItemId = itemId;
  }

  /**
   * Retrieves the current item ID from the internal state.
   *
   * @returns The ID of the current item, or undefined if not set.
   */
  get currentItemId(): string | undefined {
    // Return the current item ID.
    return this.state.currentItemId;
  }

  /**
   * Loads an item by its ID and updates the items list.
   * @param itemId The ID of the item to load.
   * @throws Error if the inventory is not available.
   */
  async loadItem(itemId: string): Promise<void> {
    // Check if the inventory is available
    if (this.inventory.value) {
      // Fetch the item from the server
      const item = await lastValueFrom(
        this.itemService.adminFindById({
          itemId,
          inventoryId: this.inventory.value.id,
        }),
      );
      if (this.inventory.value.id !== item.inventoryId)
        throw new Error("Item does not belong to this inventory");

      // If the item is fetched successfully
      if (item) {
        // Get the current list of items
        const items = this.items.value;

        // If the items list is available
        if (items) {
          // Find the index of the item in the list
          const index = items.findIndex((i) => i.id === item.id);

          // If the item exists in the list, update it
          if (index !== -1) {
            items[index] = item;
          } else {
            // If the item does not exist in the list, add it
            items.push(item);
          }

          // Update the items list with the new or updated item
          this.items.next(items);
        }
      } else {
        // Throw an error if the item is not found
        throw new Error("Item not found");
      }
    } else {
      // Throw an error if the inventory is not available
      throw new Error("Inventory is not available");
    }
  }

  /**
   * Updates an item in the items list.
   * @param item The item to update.
   * @throws Error if the item does not belong to the current inventory or if the items list is not available.
   */
  updateItem(item: ItemWithRelations): void {
    // Check if the inventory is available and if the item belongs to the current inventory
    if (!this.inventory.value || this.inventory.value.id !== item.inventoryId) {
      throw new Error(
        "Item does not belong to this inventory or inventory is not available",
      );
    }

    // Check if the items list is available
    if (this.items.value) {
      // Get the current list of items
      const items = this.items.value;

      // Find the index of the item in the list
      const index = items.findIndex((i) => i.id === item.id);

      // If the item exists in the list, update it
      if (index !== -1) {
        items[index] = item;
        this.items.next(items);
      } else {
        // If the item does not exist in the list, add it
        items.push(item);
        this.items.next(items);
      }
    } else {
      // Throw an error if the items list is not available
      throw new Error("Items list is not available");
    }
  }

  async loadTagGroups(forceReload = false): Promise<void> {
    if ((!this.tagGroups.value || forceReload) && this.inventory.value) {
      this.tagGroups.next(undefined);
      this.loading.next({ ...this.loading.value, tags: true });
      const tagGroups = await lastValueFrom(
        this.inventoryService.getInventoryTags({
          id: this.inventory.value.id,
          includeTags: true,
          nested: true,
        }),
      );
      this.tagGroups.next(tagGroups);
      this.loading.next({ ...this.loading.value, tags: false });
    }
  }

  getTagGroups(): TagGroupWithRelations[] | undefined {
    return this.tagGroups.value;
  }

  async loadLocations(forceReload = false): Promise<void> {
    if ((!this.locations.value || forceReload) && this.inventory.value) {
      this.locations.next(undefined);
      this.loading.next({ ...this.loading.value, locations: true });
      const locations = await lastValueFrom(
        this.inventoryService.getInventoryLocations({
          id: this.inventory.value.id,
          nested: true,
        }),
      );
      const locationsFlat = await lastValueFrom(
        this.inventoryService.getInventoryLocations({
          id: this.inventory.value.id,
          nested: false,
        }),
      );
      this.locations.next(locations);
      this.locationsFlat.next(locationsFlat);
      this.loading.next({ ...this.loading.value, locations: false });
    }
  }

  getLocations(): LocationWithRelations[] | undefined {
    return this.locations.value;
  }

  async loadSeries(forceReload = false): Promise<void> {
    if ((!this.series.value || forceReload) && this.inventory.value) {
      this.series.next(undefined);
      this.loading.next({ ...this.loading.value, series: true });
      const series = await lastValueFrom(
        this.adminInventoryService.adminGetSeries({
          inventoryId: this.inventory.value.id,
          lang: "cs",
        }),
      );
      this.series.next(series);
      this.loading.next({ ...this.loading.value, series: false });
    }
  }

  getSeries(): SeriesWithRelations[] | undefined {
    return this.series.value;
  }

  async loadAuctions(forceReload = false): Promise<void> {
    if ((!this.auctions.value || forceReload) && this.inventory.value) {
      this.auctions.next(undefined);
      const auctions = await lastValueFrom(
        this.auctionsService.inventoryGetAuctions({
          inventoryId: this.inventory.value.id,
        }),
      );
      this.auctions.next(auctions);
    }
  }

  getAuctions(): AuctionWithRelations[] | undefined {
    return this.auctions.value;
  }

  async loadExhibitions(forceReload = false): Promise<void> {
    if ((!this.exhibitions.value || forceReload) && this.inventory.value) {
      this.exhibitions.next(undefined);
      const exhibitions = await lastValueFrom(
        this.exhibitionsService.inventoryGetExhibitions({
          inventoryId: this.inventory.value.id,
        }),
      );
      this.exhibitions.next(exhibitions);
    }
  }

  getExhibitions(): ExhibitionWithRelations[] | undefined {
    return this.exhibitions.value;
  }

  async loadPublications(forceReload = false): Promise<void> {
    if ((!this.publications.value || forceReload) && this.inventory.value) {
      this.publications.next(undefined);
      const publications = await lastValueFrom(
        this.publicationsService.inventoryGetPublications({
          inventoryId: this.inventory.value.id,
        }),
      );
      this.publications.next(publications);
    }
  }

  getPublications(): PublicationWithRelations[] | undefined {
    return this.publications.value;
  }

  updateState(newState: InventoryState): void {
    this.state = { ...this.state, ...newState };
  }

  getState(): InventoryState {
    return this.state;
  }

  resetState(): void {
    this.state = {};
  }

  /**
   * Constructs a filterable string from an item based on specified filter settings.
   * This string is used to match against search queries in a filter operation.
   *
   * @param {ItemWithRelations} item - The item from which data is extracted.
   * @param {ItemFilterSettings} filterSettings - Settings that define which fields of the item should be included in the filter string.
   * @returns {string} A string derived from the item's fields as specified by the filter settings. This string is used for filtering operations.
   */
  getFilterDataString(
    item: ItemWithRelations,
    filterSettings: ItemSearchSettings = DEFAULT_SEARCH_SETTINGS,
  ): string {
    let dataString = "";

    // Append the original title if included in the filter settings
    if (filterSettings.fields.inventoryCode && item.inventoryCode)
      dataString += item.inventoryCode + " ";

    // Append the original title if included in the filter settings
    if (filterSettings.fields.originalTitle)
      dataString += item.originalTitle + " ";

    // Append authors' names if included, formatted as "firstname lastname nickname"
    if (filterSettings.fields.authors && item.authors?.length > 0) {
      dataString +=
        item.authors
          ?.map((a) => `${a.firstname} ${a.lastname} ${a.nickname}`)
          .join(" ") + " ";
    }

    // Append titles in specified languages if included
    if (filterSettings.fields.titleCS && item.translations[0].title)
      dataString += item.translations[0].title + " ";
    if (filterSettings.fields.titleEN && item.translations[1].title)
      dataString += item.translations[1].title + " ";

    // Append descriptions in specified languages if included
    if (filterSettings.fields.descriptionCS && item.translations[0].notesPublic)
      dataString += item.translations[0].notesPublic + " ";
    if (filterSettings.fields.descriptionEN && item.translations[1].notesPublic)
      dataString += item.translations[1].notesPublic + " ";

    // Append techniques in specified languages if included
    if (filterSettings.fields.techniqueCS && item.translations[0].technique)
      dataString += item.translations[0].technique + " ";
    if (filterSettings.fields.techniqueEN && item.translations[1].technique)
      dataString += item.translations[1].technique + " ";

    // Append substrates in specified languages if included
    if (filterSettings.fields.substrateCS && item.translations[0].substrate)
      dataString += item.translations[0].substrate + " ";
    if (filterSettings.fields.substrateEN && item.translations[1].substrate)
      dataString += item.translations[1].substrate + " ";

    if (filterSettings.fields.datingTextCS && item.translations[0].dating)
      dataString += item.translations[0].dating + " ";
    if (filterSettings.fields.datingTextEN && item.translations[1].dating)
      dataString += item.translations[1].dating + " ";

    // Append dating information if included
    if (filterSettings.fields.dating && item.datingOrder)
      dataString += String(item.datingOrder) + " ";

    // Check if the filter setting for other titles is enabled and if the item has titles
    if (filterSettings.other.titleOther && item.titles?.length > 0) {
      // Concatenate all titles into a single string and append it to the data string
      dataString +=
        item.titles
          ?.map((t) => t.title) // Map each title object to its title string
          .join(" ") + " "; // Join all title strings with a space and add a trailing space
    }

    if (filterSettings.other.notesInternal && item.notesInternal) {
      dataString += item.notesInternal + " ";
    }

    // Remove diacritics from the entire string if specified in the filter settings
    if (filterSettings.general.ignoreDiacritics) {
      dataString += this.removeDiacritics(dataString);
    }

    // Convert to lower case if case sensitivity is not required and adds to the string
    if (!filterSettings.general.caseSensitive) {
      dataString += dataString.toLowerCase();
    }

    // Trim the string to remove any extra spaces at the ends
    return dataString.trim();
  }

  /**
   * Merges two tags within the inventory by calling the mergeInventoryTags method
   * from the inventoryService. This method uses the lastValueFrom utility to convert
   * the Observable returned by the service call into a Promise.
   *
   * @param {string} keepTagId - The ID of the tag to keep.
   * @param {string} mergeTagId - The ID of the tag to merge into the keep tag.
   * @returns {Promise<void>} - A promise that resolves when the merge operation is complete.
   */
  async mergeTags(keepTagId: string, mergeTagId: string): Promise<void> {
    await lastValueFrom(
      this.inventoryService.mergeInventoryTags({
        keepTagId,
        mergeTagId,
        inventoryId: this.inventory.value.id,
      }),
    );
  }

  /**
   * Removes diacritics (accents) from a string.
   * This is useful for normalizing strings for comparison, especially in filtering and searching.
   *
   * @param {string} str - The string from which diacritics will be removed.
   * @returns {string} A new string equivalent to the original but without diacritics.
   */
  private removeDiacritics(str: string): string {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }
}
